import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import {
  transformCommunityKitchenData,
  transformAnimalShelterData,
  transformFloodReliefCampData,
  getTimestamp
} from './excelUtilsIncidentFacilities';

/**
 * Generate Excel for Incident Facilities Reports (Matching Web UI Format)
 */
export const generateIncidentFacilitiesExcel = async (options) => {
  const { data, toolName, filename } = options;

  console.log('📊 generateIncidentFacilitiesExcel called');
  console.log('- Tool name:', toolName);
  console.log('- Data records:', data?.length || 0);

  let transformedResult;
  let reportTitle;
  let filenamePrefix;

  // Normalize tool name to lowercase
  const normalizedToolName = toolName?.toLowerCase() || '';

  // Determine report type and transform data
  switch (normalizedToolName) {
    // ✅ COMMUNITY KITCHEN
    case 'community_kitchen':
    case 'communitykitchen':
    case 'incident_community_kitchen_report':
    case 'incidentcommunitykitchenreport':
    case 'incident_community_kitchen':
    case 'incidentcommunitykitchen':
      transformedResult = transformCommunityKitchenData(data);
      reportTitle = 'Community Kitchen Report';
      filenamePrefix = 'CommunityKitchen';
      break;

    // ✅ ANIMAL SHELTER
    case 'animal_shelter':
    case 'animalshelter':
    case 'incident_animal_shelter_report':
    case 'incidentanimalshelterreport':
    case 'incident_animal_shelter':
    case 'incidentanimalshelter':
      transformedResult = transformAnimalShelterData(data);
      reportTitle = 'Animal Shelter Report';
      filenamePrefix = 'Animalshelter';  // Match Web UI filename
      break;

    // ✅ FLOOD RELIEF CAMP
    case 'flood_relief_camp':
    case 'floodreliefcamp':
    case 'incident_flood_relief_camp_report':
    case 'incidentfloodreliefcampreport':
    case 'incident_flood_relief_camp':
    case 'incidentfloodreliefcamp':
      transformedResult = transformFloodReliefCampData(data);
      reportTitle = 'Flood Relief Camp Report';
      filenamePrefix = 'FloodReliefCamp';
      break;

    default:
      console.error('❌ Unknown incident facilities tool:', toolName);
      console.error('💡 Available tools: community_kitchen, animal_shelter, flood_relief_camp');
      throw new Error(`Unsupported tool: ${toolName}. Please use one of: community_kitchen, animal_shelter, flood_relief_camp`);
  }

  const { columns, transformedData } = transformedResult;

  if (transformedData.length === 0) {
    alert('No data available to export');
    return;
  }

  // Create worksheet data
  const wsData = [
    columns.map(col => col.label),
    ...transformedData.map(row => columns.map(col => row[col.label]))
  ];

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // NO HEADER STYLING - Match Web UI (plain headers)

  // Set column widths
  ws['!cols'] = columns.map((col) => {
    if (col.label.includes('Facility') || col.label.includes('District')) return { wch: 30 };
    if (col.label.includes('Block')) return { wch: 25 };
    if (col.label.includes('Date') || col.label.includes('on') || col.label.includes('On')) return { wch: 20 };
    if (col.label.includes('Status')) return { wch: 10 };
    if (col.label.includes('Fodder')) return { wch: 25 };
    return { wch: 15 };
  });

  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, reportTitle);

  // Generate filename matching Web UI format
  const finalFilename = `${filenamePrefix}_${getTimestamp()}.xlsx`;

  console.log('🔍 Attempting to download:', finalFilename);

  try {
    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
      compression: true
    });

    console.log('✅ Excel binary generated:', wbout.byteLength, 'bytes');

    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    console.log('✅ Blob created:', blob.size, 'bytes');

    saveAs(blob, finalFilename);

    console.log(`✅ ${reportTitle} Excel downloaded:`, finalFilename);
  } catch (error) {
    console.error('❌ Excel download failed:', error);
    console.error('Error details:', error.message, error.stack);
    alert(`Failed to download Excel: ${error.message}`);
  }
};
